import { createContext, useContext, useEffect, useRef, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, deleteUserToken } from "./firebase";
import { AuthPayload } from "../definitions";
import { backfillUserIdentity, getUser, getLoggedInUser, setLoggedInUser, auditUpdate, clearDbCache } from "./Database";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { saveAccount } from "./accountSwitcher";
import { claimAttendanceSupporters } from "./fixtureAttendanceLinks";
import { watchSystemAdmin } from "./teamAccess";

export const AuthContext = createContext<AuthPayload>({
  user: null,
  loading: true,
  isAdmin: false
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUid, setAdminUid] = useState<string | null>(null);
  const healedIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    setIsAdmin(false);
    clearDbCache();
    if (!user?.uid || user.isAnonymous) return;
    let active = true;
    const unsubscribe = watchSystemAdmin(user.uid, trustedAdmin => {
      if (!active) return;
      setIsAdmin(trustedAdmin);
      setAdminUid(user.uid);
      const current = getLoggedInUser();
      setLoggedInUser({ user, loading: false, isAdmin: trustedAdmin },
        current.user?.uid === user.uid ? current.dbUser : undefined);
      void saveAccount(user, trustedAdmin).catch(error => {
        console.error('Unable to update saved account permissions:', error);
      });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [user?.uid]);

  const mapNativeUserToFirebaseUser = (nativeUser: any): User => ({
    uid: nativeUser.uid,
    email: nativeUser.email || null,
    emailVerified: nativeUser.emailVerified || false,
    displayName: nativeUser.displayName || null,
    photoURL: nativeUser.photoUrl || nativeUser.photoURL || nativeUser.photo || nativeUser.providerData?.find((provider: any) => provider.photoUrl || provider.photoURL)?.photoUrl || nativeUser.providerData?.find((provider: any) => provider.photoUrl || provider.photoURL)?.photoURL || null,
    isAnonymous: nativeUser.isAnonymous || false,
    metadata: {
      creationTime: new Date(nativeUser.metadata?.creationTime || 0).toISOString(),
      lastSignInTime: new Date(nativeUser.metadata?.lastSignInTime || 0).toISOString(),
    },
    providerData: nativeUser.providerData || [],
    phoneNumber: nativeUser.phoneNumber || null,
    tenantId: nativeUser.tenantId || null,
    providerId: nativeUser.providerId || 'firebase',
  } as any as User);

  useEffect(() => {
    if (!user?.uid || !user.email) return;
    claimAttendanceSupporters().catch((error) => {
      console.warn('Failed to claim attendance supporter records:', error);
    });
  }, [user?.uid, user?.email]);

  // Runs for both the web and native auth paths, so a session restore is enough to
  // repair a user record that a partial sign-in left without a name or email.
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return;
    if (!user.displayName && !user.email && !user.photoURL) return;
    const key = `${uid}:${user.displayName ?? ''}:${user.email ?? ''}:${user.photoURL ?? ''}`;
    if (healedIdentityRef.current === key) return;
    healedIdentityRef.current = key;

    getUser(uid, undefined, true)
      .then((dbUser) => backfillUserIdentity(uid, user, dbUser))
      .catch((error) => console.warn('Failed to backfill user identity:', error));
  }, [user?.uid, user?.displayName, user?.email, user?.photoURL]);

  useEffect(() => {
    // console.log('🔵 AuthProvider: Setting up onAuthStateChanged listener...');

    // Set a timeout to ensure we don't stay in loading state forever
    const authLoadingTimeoutMs = Capacitor.isNativePlatform() ? 10000 : 5000;
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ Auth loading timeout - proceeding without auth');
      setLoading(false);
    }, authLoadingTimeoutMs);

    // On native platforms, check for existing user immediately on startup
    if (Capacitor.isNativePlatform()) {
      // console.log('🔵 AuthProvider: Checking for existing native user on startup...');
      FirebaseAuthentication.getCurrentUser()
        .then((result) => {
          if (result?.user) {
            console.log('✅ AuthProvider: Found existing native user on startup:', result.user.uid);
            const syntheticUser = mapNativeUserToFirebaseUser(result.user);

            setUser(syntheticUser);
            setLoading(false);
            clearTimeout(loadingTimeout);

            // Also trigger web SDK sync in background
            auth.currentUser?.reload().catch(() => { });
          } else {
            // console.log('🔵 AuthProvider: No existing native user found');
          }
        })
        .catch((error) => {
          console.error('🔴 AuthProvider: Failed to check for existing user:', error);
        });
    }

    // On native platforms, also listen to native auth state changes
    let nativeAuthListener: any = null;
    if (Capacitor.isNativePlatform()) {
      // console.log('🔵 AuthProvider: Setting up native auth state listener...');
      try {
        nativeAuthListener = FirebaseAuthentication.addListener('authStateChange', async (change) => {
          // console.log('🔵 AuthProvider: Native auth state changed:', change);

          // Manually trigger the web SDK's onAuthStateChanged by updating the user state
          if (change.user) {
            console.log('✅ AuthProvider: Syncing native user to context:', change.user.uid);
            setUser(mapNativeUserToFirebaseUser(change.user));
            setLoading(false);

            // A Web SDK refresh is best-effort only. It must not delay native
            // authentication because it can stall in the iOS WKWebView.
            void auth.currentUser?.reload().catch(() => { });
          } else {
            // User signed out
            // console.log('🔵 AuthProvider: Native user signed out');
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
          }
        });
        console.log('✅ AuthProvider: Native auth listener registered successfully');
      } catch (error) {
        console.error('🔴 AuthProvider: Failed to setup native auth listener:', error);
        console.error('🔴 Error details:', JSON.stringify(error));
        // Continue with web SDK only
      }
    }

    let unsubscribe: (() => void) | undefined;
    // console.log('🔵 AuthProvider: Setting up web SDK onAuthStateChanged listener...');
    unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      clearTimeout(loadingTimeout); // Clear timeout if auth loads successfully
      // console.log('🔵 AuthProvider: onAuthStateChanged fired, user:', currentUser ? currentUser.uid : 'null');
      setUser((previousUser) => {
        if (!currentUser) return null;
        return currentUser.photoURL || !previousUser?.photoURL
          ? currentUser
          : { ...currentUser, photoURL: previousUser.photoURL } as User;
      });
      setLoading(false);
      if (currentUser) {
        saveAccount(currentUser, false).catch(() => { });

        // Log session restores for returning users. Explicit logins are already
        // logged by useAuthActions; if lastSignInTime is >60 s in the past this
        // is a cookie/token restore rather than a just-completed sign-in.
        const lastSignInMs = currentUser.metadata?.lastSignInTime
          ? new Date(currentUser.metadata.lastSignInTime).getTime()
          : NaN;
        if (!isNaN(lastSignInMs) && Date.now() - lastSignInMs > 60_000) {
          auditUpdate({
            action: "user_login",
            user: currentUser.uid,
            details: `${currentUser.email || currentUser.uid} session restored`,
            timestamp: new Date().toISOString(),
          });
        }

        getUser(currentUser.uid).then(dbUser => {
          let newUserRec: User | null = currentUser;
          if (dbUser && (!currentUser?.displayName || !currentUser?.photoURL)) {
            newUserRec = {
              ...currentUser,
              displayName: currentUser.displayName || dbUser.displayName,
              photoURL: currentUser.photoURL || dbUser.photoURL || null,
            };
            setUser(newUserRec);
          }
          setLoggedInUser({
            user: newUserRec, loading: false,
            isAdmin: getLoggedInUser().user?.uid === currentUser.uid && getLoggedInUser().isAdmin,
          }, dbUser);
        }).catch(error => {
          console.error('🔴 AuthProvider: Error fetching user from database:', error);
          // Continue without database user info - auth will still work
        });
      } else if (Capacitor.isNativePlatform()) {
        // On iOS/Android the web layer can briefly emit null even when
        // native auth is already signed in. Re-check native auth before logout.
        FirebaseAuthentication.getCurrentUser()
          .then((nativeResult) => {
            if (nativeResult?.user) {
              // console.log('🔵 AuthProvider: Ignoring transient web null, native user still signed in:', nativeResult.user.uid);
              setUser(mapNativeUserToFirebaseUser(nativeResult.user));
              setLoading(false);
              return;
            }
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            deleteUserToken();
          })
          .catch((error) => {
            console.error('🔴 AuthProvider: Failed native auth check after web null state:', error);
            deleteUserToken();
          });
      } else {
        deleteUserToken();
      }
    }, (error) => {
      clearTimeout(loadingTimeout);
      console.error('🔴 AuthProvider: onAuthStateChanged error:', error);
      setLoading(false); // Still set loading to false even on error
    });

    return () => {
      clearTimeout(loadingTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
      if (nativeAuthListener) {
        nativeAuthListener.remove();
      }
    };
  }, []);

  return (
    <AuthContext value={{ user, loading, isAdmin: isAdmin && adminUid === user?.uid }}>
      {!loading && children}
    </AuthContext>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}